import React, { useEffect, useRef, useState } from "react";

import {
  AnyFilterExpressionInput,
  SortInput,
  TablesDetailsInput,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { exportExcel, findObjectVal, formatDateString } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import { LazyState, TableColumn } from "../utils/types";
import { formatSort, userTableDetails } from "../utils/utils";
import TableFilters from "./TableFilters";

type LazyTableProps = {
  data: any[];
  tableName: string;
  containerName: string;
  columns: TableColumn<any>[];
  loading: boolean;
  onLazyEvent: React.Dispatch<React.SetStateAction<LazyState>>;
  lazyState: LazyState;
  totalItems: number;
  fetchData: (
    take?: number | null,
    skip?: number | null,
    sort?: SortInput[] | null,
    filters?: AnyFilterExpressionInput[] | null,
    cacheAndNetwork?: boolean,
    filter?: AnyFilterExpressionInput | null
  ) => Promise<any>;
  buttons?: JSX.Element;
  onSearch?: (value: string) => void;
};

const LazyTable: React.FC<LazyTableProps> = ({
  data,
  tableName,
  containerName,
  columns,
  loading,
  onLazyEvent,
  lazyState,
  totalItems,
  fetchData,
  buttons,
  onSearch,
}) => {
  const [viewFilters, setViewFilters] = useState(false);
  const dt = useRef<DataTable>(null);
  const { userSettings } = useProjectContext();
  const tableDetails = userTableDetails(userSettings, tableName);

  useEffect(() => {
    if (tableDetails?.rows !== lazyState?.rows) {
      onLazyEvent(prev => ({ ...prev, rows: tableDetails?.rows || 10 }));
    }
  }, [userSettings]);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  const handleExportExcel = async () => {
    const sort = formatSort(lazyState.sortField, lazyState.sortOrder, tableName);
    const response: Record<string, { items: any[]}> = await fetchData(0, 0, sort)
    const tableData = Object.values(response)[0]?.items?.map(row => {
      const newData: any = {};
      columns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field ? formatDateString(findObjectVal(row, col.field)?.toString()) : col.body;
      });
      return newData;
    });

    exportExcel(tableData, tableName);
  };

  const exportCSV = async () => {
    const sort = formatSort(lazyState.sortField, lazyState.sortOrder, tableName);
    await fetchData(0, 0, sort)
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    if (tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: { tableDetails: tableDetailsInput },
      });
    }
    onLazyEvent(prev => ({ ...prev, first: e.first, page: e.page }));
    fetchData(e.rows, e.first);
  };

  const onSort = (e: DataTablePFSEvent) => {
    onLazyEvent(prev => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder, first: 0 }));
    const sort = formatSort(e.sortField, e.sortOrder, tableName);
    fetchData(tableDetails?.rows , 0, sort);
  };

  return (
    <div className="card p-0 sm:p-3">
      <Toolbar
        left={
          <div className="flex gap-3">
            <Button
              label="Filters"
              icon={viewFilters ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              className="w-10rem"
              onClick={() => setViewFilters(prev => !prev)}
            />
            {onSearch && <SearchInput onSearch={onSearch} />}
          </div>
        }
        right={
          <div className="flex gap-3">
            <Button
              type="button"
              icon="pi pi-file"
              className="p-button-outlined p-button-warning"
              title="Export to CSV"
              onClick={exportCSV}
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-warning"
              title="Export to Excel"
              onClick={handleExportExcel}
            />
            {buttons}
          </div>
        }
        className="gap-3"
      />

      <DataTable
        header={
          <div
            className={`${viewFilters ? "max-h-90rem" : "max-h-0"} overflow-hidden transition-all transition-duration-500 p-0`}
          >
            <TableFilters
              container={containerName}
              fetchData={fetchData}
              lazyState={lazyState}
              onLazyEvent={onLazyEvent}
            />
          </div>
        }
        ref={dt}
        value={data}
        stripedRows
        paginator
        rows={lazyState.rows}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        lazy
        totalRecords={totalItems}
        className="overflow-x-auto lazy-table"
        first={lazyState.first || 0}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        onPage={onPage}
        onSort={onSort}
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

const SearchInput: React.FC<{ onSearch: (value: string) => void }> = ({ onSearch }) => {
  const [search, setSearch] = useState("");

  return (
    <div className="p-inputgroup">
      <InputText
        onChange={e => setSearch(e.target.value)}
        placeholder="Search..."
        onPaste={e => setSearch(e.clipboardData.getData("text"))}
        onKeyUp={e => e.key === "Enter" && onSearch(search)}
      />
      <Button className="p-inputgroup-addon" icon="pi pi-search" onClick={() => onSearch(search)} />
    </div>
  );
};

export default LazyTable;
